import { createContext, useContext, ReactNode, useEffect } from 'react';
import { Link, Box, Button, Icon, Title } from '@nimbus-ds/components';
import { CloseIcon } from '@nimbus-ds/icons';
import { Trans } from 'react-i18next';
import { useNavegate } from 'App/hooks';
import { useResponsive } from 'commons/components/Responsive';
import { useControlCta } from 'commons/hooks/useControlCta';
import ctaImageMobile from 'domains/Orders/images/cta-activate-nuvem-pago-mobile.png';
import ctaImage from 'domains/Orders/images/cta-activate-nuvem-pago.png';
import nuvemPagoLogo from 'domains/Orders/images/nuvempago-logo.svg';
import {
  trackingNuvemPagoNudgesCtaActive,
  trackingNuvemPagoNudgesCtaClose,
  trackingNuvemPagoNudgesCtaView,
} from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';

interface CtaContextType {
  isMobile: boolean;
  handleActionPrimary: () => void;
  handleDismissCtaNudge: () => void;
}

interface CtaProps {
  children: ReactNode;
}

interface CtaImageProps {
  children?: ReactNode;
  mobileImage?: string;
  desktopImage?: string;
}

interface CtaContentProps {
  title: ReactNode;
  actionText: string;
  logo?: string;
}

interface CtaActionsProps {
  actionText: string;
}

const CtaContext = createContext<CtaContextType | null>(null);

const useCta = () => {
  const context = useContext(CtaContext);
  if (!context) {
    throw new Error('Cta compound components must be used within CtaCard');
  }
  return context;
};

const useCtaActions = () => {
  const { isCtaVisible, handleDismissCta, group } = useControlCta();
  const { goTo } = useNavegate();
  const { isMobile } = useResponsive();

  const handleActionPrimary = () => {
    goTo('/settings/payments');
    trackingNuvemPagoNudgesCtaActive(group);
  };

  const handleDismissCtaNudge = () => {
    trackingNuvemPagoNudgesCtaClose(group);
    handleDismissCta();
  };

  return {
    isCtaVisible,
    isMobile,
    handleActionPrimary,
    handleDismissCtaNudge,
  };
};

function CtaImage({
  children,
  mobileImage = ctaImageMobile,
  desktopImage = ctaImage,
}: CtaImageProps) {
  const { isMobile } = useCta();

  return (
    <Box
      width="100%"
      height="100%"
      position="relative"
      maxHeight="auto"
      maxWidth={isMobile ? '95px' : '140px'}
      data-testid="cta-image"
      pointerEvents="none"
    >
      {children || (
        <img
          src={isMobile ? mobileImage : desktopImage}
          width="100%"
          height="100%"
          className="object-fit-cover"
          alt="NuvemPago CTA"
          data-testid="cta-image-default"
        />
      )}
    </Box>
  );
}

function CtaContent({
  title,
  actionText,
  logo = nuvemPagoLogo,
}: CtaContentProps) {
  const { isMobile, handleActionPrimary } = useCta();

  return (
    <Box
      width="100%"
      height="100%"
      position="relative"
      display="flex"
      flexDirection="column"
      gap="1"
      justifyContent="flex-start"
      alignItems="flex-start"
      flexGrow="1"
      maxWidth={isMobile ? '100%' : '600px'}
      className="flex-1"
      data-testid="cta-content"
      pointerEvents="none"
    >
      <img src={logo} alt="Logo NuvemPago" data-testid="cta-logo" />
      <Box paddingRight={isMobile ? '12' : '2'}>
        <Title
          as={isMobile ? 'h6' : 'h4'}
          textAlign="left"
          fontWeight="regular"
          color="primary-textHigh"
          data-testid="cta-title"
        >
          {title}
        </Title>
      </Box>
      {isMobile && (
        <Link
          appearance="primary"
          textDecoration="none"
          onClick={handleActionPrimary}
          data-testid="cta-action-mobile"
        >
          {actionText}
        </Link>
      )}
    </Box>
  );
}

function CtaActions({ actionText }: CtaActionsProps) {
  const { isMobile, handleActionPrimary } = useCta();

  if (isMobile) return null;

  return (
    <Box
      display="flex"
      width="50%"
      height="100%"
      position="relative"
      flexDirection="column"
      gap="0-5"
      justifyContent="flex-end"
      alignItems="flex-end"
      data-testid="cta-actions"
    >
      <Button
        appearance="neutral"
        onClick={handleActionPrimary}
        data-testid="cta-action-desktop"
      >
        {actionText}
      </Button>
    </Box>
  );
}

function CtaDismiss() {
  const { handleDismissCtaNudge } = useCta();

  return (
    <Box
      position="absolute"
      top="0"
      right="0"
      padding="3"
      cursor="pointer"
      zIndex="100"
      data-testid="cta-dismiss"
      id="cta-dismiss"
    >
      <Icon
        source={<CloseIcon size="small" />}
        color="neutral-interactive"
        onClick={handleDismissCtaNudge}
      />
    </Box>
  );
}

function CtaCard({ children }: CtaProps) {
  const { isCtaVisible, isMobile, handleActionPrimary, handleDismissCtaNudge } =
    useCtaActions();

  if (!isCtaVisible) return null;

  const handleClickMobile = (e: React.MouseEvent<HTMLDivElement>) => {
    const targetId = (e.target as HTMLElement).id;
    if (targetId === 'cta-card') handleActionPrimary();
  };

  return (
    <CtaContext.Provider
      value={{ isMobile, handleActionPrimary, handleDismissCtaNudge }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={isMobile ? '2' : '7'}
        position="relative"
        backgroundColor={isMobile ? 'neutral-surface' : 'neutral-background'}
        borderRadius="3"
        paddingRight={isMobile ? '2' : '5'}
        paddingLeft={isMobile ? '2' : '5'}
        paddingY="2"
        minHeight={isMobile ? 'auto' : '120px'}
        width="100%"
        data-testid="cta-card"
        id="cta-card"
        onClick={handleClickMobile}
      >
        {children}
      </Box>
    </CtaContext.Provider>
  );
}

export function CtaActivateNuvemPago() {
  const t = useTranslationOrders();
  const { group } = useControlCta();

  useEffect(() => {
    trackingNuvemPagoNudgesCtaView(group);
  }, [group]);

  const title = (
    <Trans
      t={t}
      i18nKey="ctaActivateNuvemPago.title"
      components={{
        b: <b className="text-primary-textHigh" />,
      }}
    />
  );
  const actionText = t('ctaActivateNuvemPago.actionPrimary');

  return (
    <CtaCard>
      <CtaImage />
      <CtaContent title={title} actionText={actionText} />
      <CtaActions actionText={actionText} />
      <CtaDismiss />
    </CtaCard>
  );
}

CtaCard.Image = CtaImage;
CtaCard.Content = CtaContent;
CtaCard.Actions = CtaActions;
CtaCard.Dismiss = CtaDismiss;

export { CtaCard };
